import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Calculator, 
  DollarSign, 
  Home, 
  Calendar, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  FileText,
  Users,
  Shield,
  PiggyBank,
  CreditCard,
  MapPin,
  Star,
  Info
} from 'lucide-react';
import { AmortizationModal } from './AmortizationModal';
import type { LoanOption } from '../supreme-intelligence/utils/dataTransformer';

interface EnhancedLoanDetailsModalProps {
  loan: LoanOption | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectLoan: (loan: LoanOption) => void;
  homePrice: number;
}

export const EnhancedLoanDetailsModal: React.FC<EnhancedLoanDetailsModalProps> = ({
  loan,
  isOpen,
  onClose,
  onSelectLoan,
  homePrice
}) => {
  const [showAmortization, setShowAmortization] = useState(false);

  if (!loan) return null;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  const loanAmount = homePrice - loan.downPayment;
  const totalInterest = loan.totalPayment - loanAmount;

  const handleSelect = () => {
    onSelectLoan(loan);
    onClose();
  };

  const getQualificationColor = (strength: number) => {
    if (strength >= 90) return 'text-green-600';
    if (strength >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualificationLabel = (strength: number) => {
    if (strength >= 90) return 'Excellent';
    if (strength >= 70) return 'Good';
    if (strength >= 50) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold">{loan.programName}</DialogTitle>
                <DialogDescription className="text-lg mt-1">
                  Comprehensive loan analysis tailored to your profile
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-lg px-3 py-1">
                  {loan.eligibilityScore}% Match
                </Badge>
                {loan.recommendationRank === 1 && (
                  <Badge className="bg-green-600 text-white">
                    <Star className="w-3 h-3 mr-1" />
                    Top Choice
                  </Badge>
                )}
              </div>
            </div>
          </DialogHeader>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="payment">Payment Details</TabsTrigger>
              <TabsTrigger value="qualification">Qualification</TabsTrigger>
              <TabsTrigger value="features">Loan Features</TabsTrigger>
              <TabsTrigger value="analysis">Financial Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Key Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Key Loan Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-primary/5 rounded-lg">
                        <div className="text-2xl font-bold text-primary">
                          {formatCurrency(loan.detailedMonthlyPayment?.total || loan.monthlyPayment)}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Monthly Payment</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/5 rounded-lg">
                        <div className="text-2xl font-bold">
                          {formatPercentage(loan.interestRate)}
                        </div>
                        <div className="text-sm text-muted-foreground">Interest Rate</div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Loan Amount:</span>
                        <span className="font-medium">{formatCurrency(loanAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Down Payment:</span>
                        <span className="font-medium">{formatCurrency(loan.downPayment)} ({loan.downPaymentPercentage}%)</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Interest:</span>
                        <span className="font-medium text-orange-600">{formatCurrency(totalInterest)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cash to Close */}
                {loan.cashToClose && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PiggyBank className="w-5 h-5" />
                        Cash Required at Closing
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="text-center p-3 bg-accent/10 rounded-lg">
                        <div className="text-2xl font-bold text-accent-foreground">
                          {formatCurrency(loan.cashToClose.total)}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Cash Needed</div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Down Payment:</span>
                          <span>{formatCurrency(loan.cashToClose.downPayment)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Closing Costs:</span>
                          <span>{formatCurrency(loan.cashToClose.closingCosts)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Prepaids:</span>
                          <span>{formatCurrency(loan.cashToClose.prepaids)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Escrow Account:</span>
                          <span>{formatCurrency(loan.cashToClose.escrowAccount)}</span>
                        </div>
                        {loan.cashToClose.fundingFee && (
                          <div className="flex justify-between">
                            <span>Funding Fee:</span>
                            <span>{formatCurrency(loan.cashToClose.fundingFee)}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* User Context */}
              {loan.userContext && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Your Profile Match
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        <span>{loan.userContext.isVeteran ? 'Veteran' : 'Civilian'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        <span>{loan.userContext.isFirstTimeHomebuyer ? 'First-Time Buyer' : 'Repeat Buyer'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        <span>Credit Score: {loan.userContext.creditScore}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{loan.userContext.location.city}, {loan.userContext.location.state}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pros and Cons */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Advantages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {loan.pros.map((pro, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      Considerations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {loan.cons.map((con, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{con}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="payment" className="space-y-4">
              {loan.detailedMonthlyPayment && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calculator className="w-5 h-5" />
                        Monthly Payment Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center p-4 bg-primary/5 rounded-lg">
                        <div className="text-3xl font-bold text-primary">
                          {formatCurrency(loan.detailedMonthlyPayment.total)}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Monthly Payment</div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span>Principal & Interest:</span>
                          <span className="font-medium">{formatCurrency(loan.detailedMonthlyPayment.principalAndInterest)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Property Tax:</span>
                          <span className="font-medium">{formatCurrency(loan.detailedMonthlyPayment.propertyTax)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Home Insurance:</span>
                          <span className="font-medium">{formatCurrency(loan.detailedMonthlyPayment.homeInsurance)}</span>
                        </div>
                        {loan.detailedMonthlyPayment.pmi > 0 && (
                          <div className="flex justify-between items-center">
                            <span>PMI/MIP:</span>
                            <span className="font-medium">{formatCurrency(loan.detailedMonthlyPayment.pmi)}</span>
                          </div>
                        )}
                        {loan.detailedMonthlyPayment.hoa > 0 && (
                          <div className="flex justify-between items-center">
                            <span>HOA Fees:</span>
                            <span className="font-medium">{formatCurrency(loan.detailedMonthlyPayment.hoa)}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Payment History Projection
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Year 1 Total Payments:</span>
                            <span>{formatCurrency(loan.detailedMonthlyPayment.total * 12)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Year 5 Cumulative:</span>
                            <span>{formatCurrency(loan.detailedMonthlyPayment.total * 60)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Year 10 Cumulative:</span>
                            <span>{formatCurrency(loan.detailedMonthlyPayment.total * 120)}</span>
                          </div>
                        </div>
                        <Separator />
                        <div>
                          <div className="flex justify-between font-medium">
                            <span>30-Year Total:</span>
                            <span>{formatCurrency(loan.detailedMonthlyPayment.total * 360)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="qualification" className="space-y-4">
              {loan.qualificationAnalysis && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Qualification Strength
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center">
                        <div className={`text-4xl font-bold ${getQualificationColor(loan.qualificationAnalysis.qualificationStrength)}`}>
                          {loan.qualificationAnalysis.qualificationStrength.toFixed(0)}%
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {getQualificationLabel(loan.qualificationAnalysis.qualificationStrength)}
                        </div>
                        <Progress 
                          value={loan.qualificationAnalysis.qualificationStrength} 
                          className="mt-2"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Qualification Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span>Credit Score Required:</span>
                        <span className="font-medium">{loan.qualificationAnalysis.creditScoreRequired}+</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Front-End Ratio:</span>
                        <span className="font-medium">{formatPercentage(loan.qualificationAnalysis.frontEndRatio)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Back-End Ratio:</span>
                        <span className="font-medium">{formatPercentage(loan.qualificationAnalysis.debtToIncomeRatio)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Reserves Required:</span>
                        <span className="font-medium">
                          {loan.qualificationAnalysis.reservesRequired > 0 
                            ? `${loan.qualificationAnalysis.reservesRequired} months` 
                            : 'None'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="features" className="space-y-4">
              {loan.loanFeatures && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Loan Features
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span>Loan Type:</span>
                        <Badge variant="outline" className="capitalize">{loan.loanType}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Rate Type:</span>
                        <span className="font-medium capitalize">{loan.loanFeatures.rateType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Term:</span>
                        <span className="font-medium">{loan.loanFeatures.termYears} years</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Assumable:</span>
                        <span className={loan.loanFeatures.assumable ? 'text-green-600' : 'text-red-600'}>
                          {loan.loanFeatures.assumable ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Prepayment Penalty:</span>
                        <span className={loan.loanFeatures.prepaymentPenalty ? 'text-red-600' : 'text-green-600'}>
                          {loan.loanFeatures.prepaymentPenalty ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Info className="w-5 h-5" />
                        Program Requirements
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <span className="text-sm font-medium">Max Loan Amount:</span>
                        <div className="text-sm mt-1">
                          {loan.loanFeatures.maxLoanAmount > 0 
                            ? formatCurrency(loan.loanFeatures.maxLoanAmount)
                            : 'No limit (income-based)'}
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium">Occupancy:</span>
                        <div className="text-sm mt-1">{loan.loanFeatures.occupancyRequirement}</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="analysis" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Long-Term Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span>Total Cost (30 years):</span>
                      <span className="font-medium">{formatCurrency(loan.totalPayment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Interest Paid:</span>
                      <span className="font-medium text-orange-600">{formatCurrency(totalInterest)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Interest as % of Total:</span>
                      <span className="font-medium">{formatPercentage((totalInterest / loan.totalPayment) * 100)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Cost Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      Compared to conventional 30-year loan at market rates
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <div className="text-sm text-muted-foreground">Potential Monthly Savings</div>
                      <div className="text-lg font-bold text-green-600">
                        Varies by market conditions
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowAmortization(true)}
              >
                <Calculator className="w-4 h-4 mr-2" />
                View Amortization
              </Button>
              <Button 
                onClick={handleSelect} 
                className="bg-green-600 hover:bg-green-700"
              >
                <Home className="w-4 h-4 mr-2" />
                Select This Loan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Amortization Modal */}
      <AmortizationModal
        loan={loan}
        loanAmount={loanAmount}
        isOpen={showAmortization}
        onClose={() => setShowAmortization(false)}
      />
    </>
  );
};